import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";

const villefrancheSurMer: ChauffeurPage = {
  slug: "villefranche-sur-mer",
  title: "VTC & chauffeur privé à Villefranche-sur-Mer",
  subtitle:
    "Chauffeur privé à Villefranche-sur-Mer : escales de croisière, rade et vieille ville, à deux pas de Nice.",
  keywords: [
    "vtc villefranche-sur-mer",
    "chauffeur privé villefranche-sur-mer",
    "chauffeur escale croisière villefranche",
  ],
  hero: {
    title: "VTC & chauffeur privé à Villefranche-sur-Mer",
    subtitle:
      "L'une des plus belles rades de la Méditerranée et son port de croisière : un chauffeur pour vos escales et séjours.",
    keywords: [
      "VTC Villefranche-sur-Mer",
      "chauffeur escale croisière",
      "chauffeur rade Villefranche",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & chauffeur privé à Villefranche-sur-Mer",
    badges: ["Escales croisière", "À 10 min de Nice", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Villefranche-sur-Mer",
    items: [
      {
        title: "Spécialiste des escales croisière",
        description:
          "Rade en eau profonde où les paquebots mouillent : prise en charge au débarcadère des tenders, retour calé sur l'heure de rappel à bord.",
      },
      {
        title: "Excursions à la journée",
        description:
          "Une escale de quelques heures suffit pour Nice, Monaco, Èze ou Cap-Ferrat : circuit optimisé pour rentrer à temps au navire.",
      },
      {
        title: "Vieille ville préservée",
        description:
          "Ruelles, Rue Obscure et Citadelle largement piétonnes : dépose au plus près malgré l'étroitesse du centre historique.",
      },
      {
        title: "À deux pas de Nice",
        description:
          "Une dizaine de minutes de l'aéroport et du centre de Nice par la basse corniche, Cap-Ferrat juste à côté.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur à Villefranche-sur-Mer",
    services: [
      {
        title: "Transferts d'escale croisière",
        text: "Prise en charge au débarcadère des tenders, excursion privée et retour garanti avant l'heure de rappel à bord du paquebot.",
      },
      {
        title: "Excursions privées à la journée",
        text: "Nice, Monaco, Èze, Cap-Ferrat : itinéraire sur mesure depuis la rade, calé sur la durée de votre escale.",
      },
      {
        title: "Transferts aéroport",
        text: "Villefranche-sur-Mer ↔ aéroport de Nice avec suivi de vol, environ 20 minutes par la basse corniche.",
      },
      {
        title: "Transferts hôtel",
        text: "Dépose et reprise aux hôtels de la rade et du front de mer, bagages assistés malgré les ruelles étroites.",
      },
      {
        title: "Mise à disposition",
        text: "Chauffeur dédié pour une journée sur la Riviera, de la Citadelle aux villas de Cap-Ferrat voisin.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Port & débarcadère des tenders",
        description:
          "Point de débarquement des passagers de croisière en rade.",
      },
      {
        name: "Vieille ville",
        description:
          "Ruelles colorées et quais de la rade, largement piétonnes.",
      },
      {
        name: "Rue Obscure",
        description: "Rue couverte médiévale emblématique de la vieille ville.",
      },
      {
        name: "Citadelle Saint-Elme",
        description: "Forteresse du XVIe siècle dominant le port.",
      },
      {
        name: "Front de mer & plage",
        description: "Hôtels et plage de la rade en arc de cercle.",
      },
      {
        name: "Cap-Ferrat voisin",
        description: "Presqu'île attenante, villas et Plage de Paloma.",
      },
    ],
  },
  generic: {
    heading: "Villefranche-sur-Mer, rade profonde et escales de croisière",
    subheading:
      "L'un des mouillages les plus profonds de la Méditerranée, où descendent les passagers de croisière.",
    paragraphs: [
      "La rade de Villefranche compte parmi les plus profondes de la Méditerranée : les grands paquebots y mouillent et débarquent leurs passagers par tenders, directement au cœur du village. Pour une escale, chaque minute compte — nous prenons en charge au débarcadère et garantissons le retour avant l'heure de rappel à bord.",
      "En quelques heures d'escale, on rejoint facilement Nice, Èze, Monaco ou Cap-Ferrat. L'itinéraire est calé sur la durée précise de l'escale, avec une marge de sécurité pour ne jamais manquer le navire. Le chauffeur connaît les contraintes de circulation de la basse corniche en saison.",
      "La vieille ville, avec ses ruelles, la fameuse Rue Obscure et la Citadelle Saint-Elme, est en grande partie piétonne. Le chauffeur dépose au plus près des quais et reprend à la demande, sans recherche de place dans un centre historique resserré.",
    ],
    bullets: [
      "Retour avant rappel bord",
      "Escale optimisée",
      "À 10 min de Nice",
    ],
  },
  process: {
    heading: "Réserver un chauffeur à Villefranche-sur-Mer",
    steps: [
      "Indiquez votre escale (navire, heure de débarquement, heure de rappel à bord) ou vos adresses.",
      "Nous construisons un circuit qui tient dans la durée de l'escale, avec marge de sécurité.",
      "Le chauffeur vous accueille au débarcadère des tenders ou à votre hôtel.",
      "Excursion privée à votre rythme et retour garanti à temps au navire.",
    ],
  },
  faq: [
    {
      q: "Récupérez-vous les passagers de croisière au débarcadère de Villefranche ?",
      a: "Oui. Les paquebots mouillent en rade et débarquent par tenders au cœur du village ; nous accueillons au débarcadère et calons l'excursion sur votre heure de rappel à bord.",
    },
    {
      q: "Que peut-on visiter pendant une escale à Villefranche-sur-Mer ?",
      a: "Selon la durée de l'escale, Nice, Èze, Monaco et Cap-Ferrat sont tous accessibles. Nous bâtissons un circuit privé sur mesure avec une marge confortable pour le retour au navire.",
    },
    {
      q: "Combien de temps de Villefranche-sur-Mer à l'aéroport de Nice ?",
      a: "Comptez environ 20 minutes par la basse corniche hors heures de pointe. Nous suivons votre vol pour ajuster l'heure de prise en charge.",
    },
    {
      q: "Peut-on circuler facilement dans la vieille ville de Villefranche ?",
      a: "Le centre historique, avec la Rue Obscure et la Citadelle, est en grande partie piéton et étroit. Le chauffeur dépose au plus près des quais et reprend à la demande, sans recherche de stationnement.",
    },
  ],
  relatedLinks: [
    {
      label: "Transfert Aéroport de Nice ⇄ Saint-Jean-Cap-Ferrat",
      href: "/chauffeur/transferts/aeroport-nice-saint-jean-cap-ferrat",
    },
    {
      label: "Transfert Monaco ⇄ Aéroport de Nice",
      href: "/chauffeur/transferts/monaco-aeroport-nice",
    },
    {
      label: "Tous les transferts de la Côte d’Azur",
      href: "/chauffeur/transferts",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Villefranche-sur-Mer",
    text: "Escale de croisière, excursion ou transfert aéroport : réservez votre chauffeur sur la rade.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title:
      "VTC Villefranche-sur-Mer — escales croisière & rade | KS Rent Services",
    description:
      "VTC et chauffeur privé à Villefranche-sur-Mer : escales de croisière, excursions Nice/Monaco/Èze, rade et vieille ville. Retour garanti à bord, 24/7.",
    canonical: "/chauffeur/villefranche-sur-mer",
  },
};

export default villefrancheSurMer;
